import { ComponentPropsWithoutRef, ElementType, forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../../utils/cn";

export const typographyVariants = cva("font-['Mulish']", {
  variants: {
    variant: {
      h1: "text-[20px] sm:text-[32px] font-extrabold leading-[normal]",
      h2: "text-[20px] font-extrabold leading-6 tracking-[0.6px]",
      h3: "text-2xl font-semibold leading-snug",
      h4: "text-xl font-semibold leading-snug",
      body: "text-base sm:text-lg font-normal leading-[normal]",
      small: "text-sm font-normal leading-normal text-[#9d9d9d]",
    },
    color: {
      default: "text-pado",
      muted: "text-[#9d9d9d]",
      inherit: "text-inherit",
    },
  },
  defaultVariants: {
    variant: "body",
    color: "default",
  },
});

export interface ITypographyProps
  extends
    Omit<ComponentPropsWithoutRef<"p">, "color">,
    VariantProps<typeof typographyVariants> {
  as?: ElementType;
}

/**
 * Base do sistema de tipografia. Não é pensado para uso direto na maioria
 * dos casos — prefira uma variante explícita (H1, H2, ...) construída em
 * cima dela. Use Typography diretamente só quando nenhuma variante
 * explícita existente cobrir o caso.
 */
export const Typography = forwardRef<HTMLElement, ITypographyProps>(
  ({ as: Component = "p", variant, color, className, ...rest }, ref) => {
    return (
      <Component
        ref={ref}
        className={cn(typographyVariants({ variant, color }), className)}
        {...rest}
      />
    );
  },
);

Typography.displayName = "Typography";
